// src/Components/Administration/ClientReport/ClientReportChartsModule.js
import React from "react";
import { Card } from "antd";
import { Doughnut, Bar } from "react-chartjs-2";
import { DesktopOutlined, MobileOutlined } from "@ant-design/icons";

export const getDoughnutChartData = (deviceUsageData) => ({
    labels: ["Mobile Devices", "Desktop Devices"],
    datasets: [{
        data: [
            deviceUsageData.reduce((sum, event) => sum + event.devices.filter(d => d.type === "mobile").length, 0),
            deviceUsageData.reduce((sum, event) => sum + event.devices.filter(d => d.type === "desktop").length, 0)
        ],
        backgroundColor: ["#52c41a", "#0078D6"],
        borderWidth: 0
    }],
});

export const getDoughnutOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false,
        },
    },
});

export const getStackedBarChartData = (deviceUsageData) => {
    const MIN_EVENTS = 7;
    let labels = [...deviceUsageData.map(event => event.event_mnemonic)];
    let datasets = [
        {
            label: "Mobile Devices",
            data: deviceUsageData.map(event => event.devices.filter(d => d.type === "mobile").length),
            backgroundColor: "#36A2EB",
        },
        {
            label: "Desktop Devices",
            data: deviceUsageData.map(event => event.devices.filter(d => d.type === "desktop").length),
            backgroundColor: "#FF6384",
        },
    ];

    while (labels.length < MIN_EVENTS) {
        labels.push("");
        datasets.forEach(dataset => dataset.data.push(0));
    }

    return {
        labels,
        datasets,
    };
};

export const getStackedBarOptions = (textColor) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            labels: { color: textColor, font: { size: 14 } },
        },
    },
    scales: {
        x: { stacked: true, ticks: { color: textColor } },
        y: { stacked: true, ticks: { color: textColor } },
    },
});

export const DoughnutChartCard = ({ deviceUsageData, textColor }) => {
    const chartData = getDoughnutChartData(deviceUsageData);
    const chartOptions = getDoughnutOptions();

    const mobileCount = chartData.datasets[0].data[0];
    const desktopCount = chartData.datasets[0].data[1];

    return (
        <Card
            title="Mobile vs Desktop Devices"
            style={{ width: "100%", height: "100%" }}
            bodyStyle={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 16
            }}
        >
            {deviceUsageData.length > 0 && (
                <>
                    {/* 🔹 Custom legend */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "24px",
                            flexWrap: "wrap"
                        }}
                    >
                        {/* Mobile */}
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "6px 12px",
                                borderRadius: 10,
                                background: "rgba(82, 196, 26, 0.12)"
                            }}
                        >
                            <span
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    backgroundColor: "#52c41a"
                                }}
                            />
                            <MobileOutlined style={{ color: "#52c41a", fontSize: "18px" }} />
                            <span style={{ color: textColor }}>Mobile</span>
                            <strong style={{ color: textColor }}>{mobileCount}</strong>
                        </div>

                        {/* Desktop */}
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "6px 12px",
                                borderRadius: 10,
                                background: "rgba(0, 120, 214, 0.12)"
                            }}
                        >
                            <span
                                style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    backgroundColor: "#0078D6"
                                }}
                            />
                            <DesktopOutlined style={{ color: "#0078D6", fontSize: "18px" }} />
                            <span style={{ color: textColor }}>Desktop</span>
                            <strong style={{ color: textColor }}>{desktopCount}</strong>
                        </div>
                    </div>

                    {/* 🔹 Chart */}
                    <div
                        style={{
                            flex: 1,
                            minHeight: 280,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >
                        <div
                            style={{
                                width: "100%",
                                maxWidth: 320,
                                height: 320
                            }}
                        >
                            <Doughnut data={chartData} options={chartOptions} />
                        </div>
                    </div>
                </>
            )}
        </Card>
    );
};

export const StackedBarChartCard = ({ deviceUsageData, textColor }) => {
    const chartData = getStackedBarChartData(deviceUsageData);
    const chartOptions = getStackedBarOptions(textColor);

    return (
        <Card title="Devices Used per Event">
            <div style={{ margin: "auto", minHeight: "400px" }}>
                {deviceUsageData.length > 0 && (
                    <Bar data={chartData} options={chartOptions} />
                )}
            </div>
        </Card>
    );
};